"use client";

import { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Container,
  useMediaQuery,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Define TypeScript interfaces for our data
interface Brand {
  id: string;
  name: string;
}

interface Product {
  id: string;
  title: string;
  image: string;
  category: string;
  link?: string;
}

interface ProductDisplayProps {
  brands: Brand[];
  products: Product[];
  title?: string;
  link?: string;
}

export default function ProductDisplay({
  brands,
  products,
}: ProductDisplayProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("md"));
  const [selectedBrand, setSelectedBrand] = useState<string | null>("parker");

  const handleBrandSelect = (brandId: string) => {
    setSelectedBrand(brandId === selectedBrand ? brandId : brandId);
  };

  // Filter products by selected brand if any
  const filteredProducts = selectedBrand
    ? products.filter((product) => product.category === selectedBrand)
    : products;

  const drawerWidth = 240;

  const redirectURL = (brand: string | null) => {
    
    brand = brand != null ? brand.toLowerCase() : "";

    switch (brand) {
        case "parker":
            window.open("https://www.parker.com/us/en/home.html", "_blank");
            break;
        case "taiyo":
            window.open("https://www.parker.com/us/en/home.html", "_blank");
            break;
        case "calzoni":
            window.open("https://calzoni-hydraulics.com/", "_blank");
            break;
        case "denison":
            window.open("https://www.parker.com/us/en/divisions/hydraulic-pump-and-power-systems-division.html", "_blank");
            break;
        case "bondioli":
            window.open("https://bondioli-pavesi.com/en/products", "_blank");
            break;
        default:
            break;
    }
};


  const drawer = (
    <Box sx={{ overflow: "auto" }}>
      <List>
        {brands.map((brand) => (
          <ListItem
            key={brand.id}
            onClick={() => handleBrandSelect(brand.id)}
            sx={{
              borderBottom: "1px solid #eee",

              bgcolor: brand.id === selectedBrand ? "#f5f5f5" : "transparent",
              cursor: "pointer",
              rounded: "none",
              "&:hover": {
                bgcolor: brand.id === selectedBrand ? "#a00025" : "#f5f5f5",
                color: brand.id === selectedBrand ? "white" : "inherit",
              },
              py: 2,
              backgroundColor:
                brand.id === selectedBrand ? "#a00025" : "inherit",
              color: brand.id === selectedBrand ? "white" : "inherit",
            }}
          >
            <ListItemText primary={brand.name} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        position: "relative",
      }}
    >
      {(isMobile || isMedium) && (
        <div
          className=" absolute bg-gradient-to-t -top-32 z-30 h-44 items-end overflow-hidden left-0 right-0 
 flex gap-2  mt-2  whitespace-nowrap pb-1 from-[#2D3A45]  via-[#2D3A45] via-60% to-transparent
"
        >
          <div className=" min-h-20 px-4 overflow-x-auto scrollbar-hide">
            {brands.map((brand: any) => (
              <Button
                key={brand.id}
                variant={brand.id === selectedBrand ? "contained" : "outlined"}
                color="primary"
                onClick={() => handleBrandSelect(brand.id)}
                sx={{
                  flexShrink: 0, // Prevents shrinking in flexbox
                  borderRadius: "50px",
                  margin: "0.2rem",
                  minWidth: "120px", // Ensures uniform button size
                  borderColor: "white",
                  borderWidth: 0.6,
                  paddingY: 1,
                  bgcolor:
                    brand.id === selectedBrand
                      ? "white !important"
                      : "#2D3A45 !important",
                  color: brand.id === selectedBrand ? "#2D3A45" : "white",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "100 !important",
                    textTransform: "capitalize",
                  }}
                >
                  {brand.name}
                </Typography>
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className=" w-full mt-20  lg:mt-4 px-10 flex justify-end ">
        <button
          onClick={() => redirectURL(selectedBrand)}
          className="px-4 py-2 capitalize rounded-full border text-pink-700 border-pink-700"
        >
          Browse {selectedBrand} Products
        </button>
      </div>

      <Box sx={{ display: "flex", width: "100%" }}>
        {isLarge && (
          <Box
            component="nav"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              paddingLeft: 2,
            }}
          >
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: "none", md: "block" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                  position: "relative",
                  height: "100%",
                  border: "none",
                },
              }}
              open
            >
              {drawer}
            </Drawer>
          </Box>
        )}

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            mt: isMobile ? 2 : 0,
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={3}>
              {filteredProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card
                    onClick={() => window.open(product.link || "", "_blank")}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "#f5f5f5",
                      // boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-4px)",
                        transition: "all 0.3s ease",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      sx={{
                        height: 300,
                        objectFit: "fit",
                        margin: "auto",
                        width: "85%",
                        p: 2,
                        bgcolor: "#f5f5f5",
                      }}
                      image={product.image}
                      alt={product.title}
                    />
                    <CardContent sx={{ flexGrow: 1, textAlign: "center" }}>
                      <Typography gutterBottom variant="h6" component="div">
                        {product.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
